import { useState, useEffect } from "react";

interface CameraAvailability {
  isAvailable: boolean;
  isChecking: boolean;
  error: string | null;
}

export const useCameraAvailability = (): CameraAvailability => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const checkCameraAvailability = async () => {
      try {
        setIsChecking(true);
        setError(null);

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          if (isMounted) {
            setIsAvailable(false);
            setError("Camera API not supported");
            setIsChecking(false);
          }
          return;
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const hasVideoInput = devices.some((device) => device.kind === "videoinput");

          if (isMounted) {
            setIsAvailable(hasVideoInput);
            setIsChecking(false);

            if (!hasVideoInput) {
              setError("No camera devices found");
            }
          }
        } catch (enumerateError) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: "user" },
              audio: false,
            });

            if (isMounted) {
              setIsAvailable(true);
              setIsChecking(false);
            }

            stream.getTracks().forEach((track) => track.stop());
          } catch (getUserMediaError: any) {
            if (isMounted) {
              setIsAvailable(false);
              setIsChecking(false);

              if (getUserMediaError.name === "NotFoundError" || getUserMediaError.name === "DevicesNotFoundError") {
                setError("No camera found on this device");
              } else if (getUserMediaError.name === "NotSupportedError") {
                setError("Camera not supported on this device");
              } else {
                setError("Camera access not available");
              }
            }
          }
        }
      } catch (generalError) {
        if (isMounted) {
          setIsAvailable(false);
          setIsChecking(false);
          setError("Failed to check camera availability");
        }
      }
    };

    checkCameraAvailability();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    isAvailable,
    isChecking,
    error,
  };
};
